/* CONFRONTO FRA BACKTEST.
 *
 * La domanda a cui questa pagina risponde NON è «quale ha il numero più
 * grande»: quella si vede già ordinando la lista. È PERCHÉ uno è meglio
 * dell'altro — e quando non lo è, perché non lo è. Tre casi veri dai nostri
 * dati, che sono anche i tre motivi per cui la pagina è fatta così:
 *
 *  1. Due configurazioni con lo STESSO totale e comportamento opposto: su XRP a
 *     quattro ore, +15.445% contro +15.398%. Gemelle nel totale, e una guadagna
 *     il doppio dell'altra negli anni di ribasso. Il totale le mostra identiche,
 *     l'anno per anno le separa — ed è per questo che l'anno per anno affiancato
 *     è la sezione che vale più della tabella.
 *  2. Strategie diverse sullo stesso asset: la domanda utile non è quale rende
 *     di più, è DOVE una batte l'altra. Se una vince solo nel 2020–2021 e
 *     l'altra tiene nel 2022, sono due strumenti per due mercati.
 *  3. La stessa strategia su candele diverse: ETH a un'ora dà il triplo di ETH
 *     a quattro ore, ma con un intorno quasi uguale. Serve capire se il
 *     vantaggio viene dalla strategia o da quanti rimbalzi ha intercettato —
 *     ed è per questo che c'è «operazioni per anno» accanto alle operazioni.
 *
 * L'ORDINE DELLA PAGINA NON È CASUALE. Prima le riserve — periodi diversi,
 * mercati diversi, anni parziali — perché ciò che rende un confronto meno
 * affidabile va letto PRIMA dei numeri, non dopo aver già deciso. Poi il
 * verdetto con il suo perché. Poi il periodo comune, che è l'unica lettura
 * rigorosa. Poi la tabella, l'anno per anno, le curve e i parametri.
 *
 * COSA FA IL CSS E COSA FA IL JAVASCRIPT. Le due forme della tabella delle
 * metriche — schede impilate per metrica sotto i 720px, colonne affiancate
 * sopra — sono entrambe renderizzate e sceglie il CSS, come nella lista. I
 * conti che dipendono dal NUMERO DI CANDIDATI (larghezza minima delle matrici)
 * restano in JavaScript, perché sono conti sui dati e non sulla finestra.
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Avvertenza,
  Avvisi,
  ContenitoreDati,
  Distintivo,
  DistintivoPeriodo,
  IconaFreccia,
  IconaStrategia,
  IntestazionePagina,
  Pulsante,
  ScheletroLista,
  usaAvvisi,
} from '../../componenti';
import { backtest as apiBacktest } from '../../api/risorse';
import { usaRichiesta } from '../../api/usaRichiesta';
import { durataPeriodo, num, pct, periodo as etichettaPeriodo, timeframe } from '../../lib/formato';
import { tonoRendimento } from '../../lib/metriche';
import type { Backtest } from '../../api/tipi';
import { AnniAffiancati, CurveSovrapposte } from './ConfrontoAnni';
import { ConfrontoParametri } from './ConfrontoParametri';
import {
  LETTERE,
  METRICHE,
  NOMI_GRUPPO,
  analizzaAnni,
  calcolaVerdetto,
  confrontoLecito,
  esitoRiga,
  inTestaFra,
  periodiIdentici,
  rendimentoComune,
  type AnalisiAnni,
  type Candidato,
  type GruppoMetrica,
  type Metrica,
  type RendimentoComune,
} from './confronto';
import {
  MINIMO_CONFRONTO,
  TETTO_CONFRONTO,
  leggiSelezione,
  PARAM_CONFRONTO,
} from './statoConfronto';
import './confronto.css';
import './backtest.css';

/** Cosa è tornato dal server per un id: la riga, oppure il motivo per cui no. */
interface Recuperato {
  id: number;
  dato: Backtest | null;
  errore: string | null;
}

export function ConfrontoBacktest() {
  const navigate = useNavigate();
  const [parametri, setParametri] = useSearchParams();
  const { avvisi, mostra } = usaAvvisi();

  const ids = useMemo(() => leggiSelezione(parametri), [parametri]);
  /* Le dipendenze sono la CHIAVE testuale e non l'array: un array nuovo a ogni
     render rilancerebbe quattro richieste in continuazione. */
  const chiave = ids.join(',');
  const abbastanza = ids.length >= MINIMO_CONFRONTO;

  const richiesta = usaRichiesta<Recuperato[]>(
    (segnale) =>
      /* Una chiamata per candidato: `GET /backtester/:id` è l'unico endpoint che
         porta la proiezione completa — serie, `traderParams`, tutte le metriche
         derivate — e su quattro righe i megabyte si pagano una volta sola.
         L'errore è catturato PER CANDIDATO e non sulla `Promise.all`: un id
         cancellato non deve portare giù la pagina intera, diventa una colonna
         che dichiara la propria assenza. */
      Promise.all(
        ids.map(async (id): Promise<Recuperato> => {
          try {
            const dato = await apiBacktest.uno(id, segnale);
            return { id, dato: dato ?? null, errore: null };
          } catch (e) {
            /* L'annullamento deve risalire: `usaRichiesta` lo riconosce e non lo
               scrive a schermo. Trasformarlo in un errore per candidato farebbe
               comparire «non recuperato» ogni volta che si cambia selezione. */
            if (e instanceof DOMException && e.name === 'AbortError') throw e;
            return { id, dato: null, errore: e instanceof Error ? e.message : String(e) };
          }
        }),
      ),
    [chiave],
    { attiva: abbastanza, eVuoto: (v) => v.length === 0 },
  );

  const togli = useCallback(
    (id: number) => {
      const restanti = ids.filter((x) => x !== id);
      if (restanti.length === 0) {
        navigate('/backtest');
        return;
      }
      const p = new URLSearchParams();
      p.set(PARAM_CONFRONTO, restanti.join(','));
      setParametri(p);
      if (restanti.length < MINIMO_CONFRONTO) {
        mostra('Resta un candidato solo: aggiungine un altro dalla lista.', 'neutro');
      }
    },
    [ids, navigate, setParametri, mostra],
  );

  if (!abbastanza) {
    return (
      <div className="sb-cornice">
        <IntestazionePagina titolo="Confronto fra backtest" />
        <div className="sb-cf">
          <Avvertenza livello="nota">
            {ids.length === 0
              ? 'L’indirizzo non contiene nessun backtest da confrontare.'
              : `L’indirizzo contiene un backtest solo (#${ids[0]}): un confronto ne vuole almeno ${MINIMO_CONFRONTO}, al massimo ${TETTO_CONFRONTO}.`}{' '}
            Le caselle di selezione stanno nella lista, sia nella tabella sia nelle schede; dal
            dettaglio di un backtest c'è «Confronta con…», che riporta alla lista con quello già
            in mano.
          </Avvertenza>
          <div>
            <Pulsante
              variante="primario"
              onClick={() =>
                navigate(ids.length === 1 ? `/backtest?${PARAM_CONFRONTO}=${ids[0]}` : '/backtest')
              }
            >
              {ids.length === 1 ? 'Scegli con cosa confrontarlo' : 'Vai alla lista dei backtest'}
            </Pulsante>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sb-cornice">
      <Avvisi avvisi={avvisi} />

      <div style={{ padding: 'var(--sb-pad-pagina)', paddingBottom: 0 }}>
        <Pulsante variante="nudo" onClick={() => navigate(`/backtest?${PARAM_CONFRONTO}=${chiave}`)}>
          <IconaFreccia verso="sinistra" />
          Torna alla lista, con la selezione
        </Pulsante>
      </div>

      <IntestazionePagina
        titolo="Confronto fra backtest"
        sotto={
          <span className="sb-nota">
            {num(ids.length)} candidati · l'indirizzo di questa pagina contiene la selezione, quindi
            si può condividere e ritrovare
          </span>
        }
      />

      <ContenitoreDati
        stato={richiesta.stato}
        causaVuoto="Il server non ha restituito nessuna delle righe richieste."
        azioneVuoto={{ etichetta: 'Torna alla lista', onClick: () => navigate('/backtest') }}
        messaggioErrore={richiesta.errore?.message}
        onRiprova={richiesta.ricarica}
        scheletro={
          <div className="sb-cf">
            <ScheletroLista righe={5} />
          </div>
        }
      >
        {richiesta.dati && (
          <Corpo
            recuperati={richiesta.dati}
            onTogli={togli}
            onApri={(id) => navigate(`/backtest/${id}`)}
          />
        )}
      </ContenitoreDati>
    </div>
  );
}

/* ========================================================================== */

function Corpo({
  recuperati,
  onTogli,
  onApri,
}: {
  recuperati: Recuperato[];
  onTogli: (id: number) => void;
  onApri: (id: number) => void;
}) {
  const mancanti = recuperati.filter((r) => r.dato == null);

  const candidati: Candidato[] = recuperati
    .filter((r): r is Recuperato & { dato: Backtest } => r.dato != null)
    .map((r, i) => ({ id: r.id, lettera: LETTERE[i] ?? String(i + 1), dato: r.dato }));

  const analisi = useMemo(() => analizzaAnni(candidati), [candidati]);
  const comuni = useMemo(
    () => candidati.map((_, i) => rendimentoComune(analisi, i)),
    [candidati, analisi],
  );
  const verdetto = useMemo(
    () => calcolaVerdetto(candidati, analisi, comuni),
    [candidati, analisi, comuni],
  );

  if (candidati.length < MINIMO_CONFRONTO) {
    return (
      <div className="sb-cf">
        <Avvertenza livello="grave">
          Restano meno di {MINIMO_CONFRONTO} candidati leggibili:{' '}
          {mancanti
            .map((m) => `#${m.id} ${m.errore ? `non recuperato (${m.errore})` : 'non esiste più'}`)
            .join(' · ')}
          . Un confronto con una colonna sola non è un confronto.
        </Avvertenza>
      </div>
    );
  }

  const periodiUguali = periodiIdentici(candidati);

  return (
    <div className="sb-cf">
      {/* --- chi si sta confrontando --- */}
      <div className="sb-cf-candidati">
        {candidati.map((c) => (
          <SchedaCandidato key={c.id} candidato={c} onTogli={onTogli} onApri={onApri} />
        ))}
      </div>

      {mancanti.length > 0 && (
        <Avvertenza livello="cautela">
          {mancanti
            .map((m) =>
              m.errore
                ? `#${m.id} non è stato recuperato: ${m.errore}`
                : `#${m.id} non esiste più: può essere stato eliminato, o l’indirizzo è vecchio`,
            )
            .join(' · ')}
          . Il confronto qui sotto è fatto sui soli candidati rimasti.
        </Avvertenza>
      )}

      {/* --- LE RISERVE, prima dei numeri. Sotto, si leggerebbero dopo aver già
          deciso. --- */}
      {verdetto.riserve.map((r) => (
        <Avvertenza key={r} livello="cautela">
          {r}
        </Avvertenza>
      ))}

      {/* --- 1. IL VERDETTO E IL PERCHÉ --- */}
      <div className={`sb-cf-verdetto${verdetto.vincitore == null ? ' sb-cf-verdetto--pari' : ''}`}>
        <div className="sb-etichetta">
          {verdetto.vincitore == null ? 'Verdetto · nessun vincitore' : 'Verdetto'}
        </div>
        <div className="sb-cf-verdetto__frase">{verdetto.frase}</div>

        {/* Gli assi non sono una ripetizione della frase: sono la prova. La frase
            dice «batte su tenuta», qui si legge di quanto e con quali numeri. */}
        <div className="sb-cf-assi">
          {verdetto.assi.map((a) => (
            <div key={a.chiave} className="sb-cf-asse">
              <span className="sb-cf-asse__nome">{a.frase}</span>
              <span className="sb-cf-asse__valori">
                {candidati.map((c, i) => (
                  <span key={c.id} className="sb-cf-asse__valore">
                    <span className="sb-cf-lettera sb-cf-lettera--piccola">{c.lettera}</span>
                    {a.valori[i]}
                  </span>
                ))}
              </span>
              <span className="sb-cf-asse__esito">
                {a.vincitore == null
                  ? a.pari
                    ? 'pari'
                    : 'non misurato su tutti'
                  : `davanti «${candidati.find((c) => c.id === a.vincitore)?.lettera}» · ${a.scarto}`}
              </span>
            </div>
          ))}
        </div>

        {verdetto.osservazioni.map((o) => (
          <div key={o} className="sb-cf-nota" style={{ color: 'var(--sb-testo-medio)' }}>
            {o}
          </div>
        ))}
      </div>

      {/* --- 2a. IL PERIODO COMUNE: l'unica lettura rigorosa --- */}
      <PeriodoComune candidati={candidati} analisi={analisi} comuni={comuni} />

      {/* --- 2b. LA TABELLA AFFIANCATA --- */}
      <div className="sb-cf-sezione">
        <div className="sb-cf-sezione__testata">
          <span className="sb-cf-sezione__titolo">Le metriche, affiancate</span>
          <Distintivo contorno>
            {periodiUguali ? 'stesso periodo per tutti' : 'periodi diversi'}
          </Distintivo>
        </div>
        <TabellaMetriche candidati={candidati} periodiUguali={periodiUguali} />
      </div>

      {/* --- 3. ANNO PER ANNO --- */}
      <div className="sb-cf-sezione">
        <div className="sb-cf-sezione__testata">
          <span className="sb-cf-sezione__titolo">Anno per anno, affiancato</span>
          <span className="sb-cf-nota">
            colore = segno · grassetto = ha battuto il mercato · sottolineatura blu = ha reso più
            degli altri candidati
          </span>
        </div>
        <AnniAffiancati candidati={candidati} analisi={analisi} />
        <div className="sb-cf-sezione__corpo" style={{ paddingTop: 0 }}>
          <div className="sb-cf-nota">
            Gli anni segnati a sinistra col filo blu sono quelli del <b>periodo comune</b>: quelli
            che tutti i candidati hanno affrontato per la stessa fetta di calendario. Gli altri
            restano visibili col motivo dell'esclusione, perché toglierli farebbe sembrare più
            corta la storia di chi ne ha di più.
          </div>
          {/* Lo scorrimento orizzontale è dichiarato, non lasciato scoprire: su
              telefono una matrice a quattro colonne non entra, e impilarla per
              anno la trasformerebbe in quattro elenchi separati — cioè
              esattamente la cosa che questa sezione serve a non fare. */}
          <div className="sb-cf-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
            Su schermo stretto la matrice <b>scorre in orizzontale</b>: la colonna dell'anno resta
            agganciata a sinistra, così il riferimento non esce mai di scena.
          </div>
        </div>
      </div>

      {/* --- 4. LE CURVE SOVRAPPOSTE --- */}
      <div className="sb-cf-sezione">
        <div className="sb-cf-sezione__testata">
          <span className="sb-cf-sezione__titolo">Curve del capitale, sovrapposte</span>
        </div>
        <CurveSovrapposte candidati={candidati} onApri={onApri} />
      </div>

      {/* --- 5. I PARAMETRI --- */}
      <div className="sb-cf-sezione">
        <div className="sb-cf-sezione__testata">
          <span className="sb-cf-sezione__titolo">I parametri, a confronto</span>
        </div>
        <ConfrontoParametri candidati={candidati} />
      </div>
    </div>
  );
}

/* ==========================================================================
 * LA SCHEDA DI UN CANDIDATO
 * ======================================================================== */

function SchedaCandidato({
  candidato: c,
  onTogli,
  onApri,
}: {
  candidato: Candidato;
  onTogli: (id: number) => void;
  onApri: (id: number) => void;
}) {
  const periodo = etichettaPeriodo(c.dato.startDate, c.dato.endDate);
  const durata = durataPeriodo(c.dato.startDate, c.dato.endDate);

  return (
    <div className="sb-cf-candidato">
      <div className="sb-cf-candidato__testata">
        <span className="sb-cf-lettera">{c.lettera}</span>
        <IconaStrategia nome={c.dato.strategyName} dim={22} />
        <span className="sb-cf-candidato__nome">{c.dato.strategyName}</span>
      </div>
      <div className="sb-cf-candidato__sotto">
        {c.dato.asset}/{c.dato.currency} · {timeframe(c.dato.candleSize)} · backtest #{c.id}
        {c.dato.backtestGroupId != null && ` · gruppo #${c.dato.backtestGroupId}`}
      </div>
      {/* Il periodo su OGNI colonna, sempre: è il metro con cui si legge ogni
          numero della colonna, e due colonne allineate senza il loro periodo
          sono la cosa che questa pagina esiste per impedire. */}
      <div>
        <DistintivoPeriodo>
          {periodo}
          {durata ? ` · ${durata}` : ''}
        </DistintivoPeriodo>
      </div>
      {c.dato.status === 'RUNNING' && (
        <Distintivo tono="cautela">in calcolo: numeri parziali</Distintivo>
      )}
      {c.dato.status === 'ERROR' && <Distintivo tono="perdita">interrotto con errore</Distintivo>}
      {c.dato.wasStoppedEarly && (
        <Distintivo tono="cautela">fermato prima della fine</Distintivo>
      )}
      <div className="sb-cf-candidato__azioni">
        <Pulsante variante="tenue" onClick={() => onApri(c.id)}>
          Apri il dettaglio
        </Pulsante>
        <Pulsante variante="nudo" onClick={() => onTogli(c.id)}>
          Togli
        </Pulsante>
      </div>
    </div>
  );
}

/* ==========================================================================
 * IL PERIODO COMUNE
 * ======================================================================== */

function PeriodoComune({
  candidati,
  analisi,
  comuni,
}: {
  candidati: Candidato[];
  analisi: AnalisiAnni;
  comuni: RendimentoComune[];
}) {
  const anni = analisi.comuni.map((a) => a.anno);

  return (
    <div className="sb-cf-sezione">
      <div className="sb-cf-sezione__testata">
        <span className="sb-cf-sezione__titolo">Sul periodo comune — il confronto rigoroso</span>
        {anni.length > 0 && (
          <Distintivo contorno>
            {anni.length === 1 ? `${anni[0]}` : `${anni[0]}–${anni[anni.length - 1]}`} ·{' '}
            {num(analisi.anniEffettivi, 2)} anni misurati
          </Distintivo>
        )}
      </div>

      <div className="sb-cf-sezione__corpo">
        {analisi.perche != null ? (
          <>
            <Avvertenza livello="grave">
              Il confronto sul periodo comune <b>non si può fare</b>. {analisi.perche}
            </Avvertenza>
            <div className="sb-cf-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
              Resta il confronto generico, qui sotto: vale solo per le metriche già normalizzate —
              CAGR, a pari rischio, intorno, Sharpe, Calmar, operazioni vinte — e la tabella lo
              dichiara riga per riga.
            </div>
          </>
        ) : (
          <>
            <div className="sb-cf-nota" style={{ marginBottom: 'var(--sb-s-6)' }}>
              Composto dai <b>rendimenti annuali</b> dei soli {num(anni.length)} anni che tutti i
              candidati hanno affrontato per la stessa fetta di calendario: {anni.join(', ')}.
              L'annualizzato divide per {num(analisi.anniEffettivi, 2)} anni, che è la durata vera —
              un anno coperto a metà conta metà. È l'unica riga di questa pagina in cui «A è meglio
              di B» significa qualcosa, perché è l'unica in cui A e B hanno visto lo stesso mercato.
            </div>

            <div className="sb-scorri-x">
              <table className="sb-cf-anni" style={{ minWidth: 200 + candidati.length * 130 }}>
                <thead>
                  <tr>
                    <th>Sul periodo comune</th>
                    {candidati.map((c) => (
                      <th key={c.id}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span className="sb-cf-lettera sb-cf-lettera--piccola">{c.lettera}</span>
                          #{c.id}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Qui il migliore SI evidenzia, e senza riserve: è l'unica
                      tabella della pagina in cui i numeri sono misurati sullo
                      stesso tratto di mercato. La soglia di pareggio è la stessa
                      della tabella generica — due soglie diverse per la stessa
                      domanda darebbero due verdetti dentro la stessa pagina. */}
                  <RigaComune
                    nome="Rendimento composto"
                    valori={comuni.map((c) => (c.composto == null ? 'n/d' : pct(c.composto)))}
                    toni={comuni.map((c) => tonoRendimento(c.composto))}
                    inTesta={inTestaFra(comuni.map((c) => c.composto))}
                    candidati={candidati}
                  />
                  <RigaComune
                    nome="Annualizzato"
                    valori={comuni.map((c) =>
                      c.annualizzato == null ? 'n/d' : pct(c.annualizzato, 1),
                    )}
                    toni={comuni.map((c) => tonoRendimento(c.annualizzato))}
                    inTesta={inTestaFra(comuni.map((c) => c.annualizzato))}
                    candidati={candidati}
                  />
                  {/* Il mercato è il metro, non un merito: nessuno «in testa». */}
                  <RigaComune
                    nome="Mercato, stessi anni"
                    valori={comuni.map((c) => (c.mercato == null ? 'n/d' : pct(c.mercato)))}
                    toni={comuni.map((c) => tonoRendimento(c.mercato))}
                    inTesta={null}
                    candidati={candidati}
                  />
                  <RigaComune
                    nome="Anni chiusi sopra il mercato"
                    valori={comuni.map((c) =>
                      c.anniSopraMercato == null
                        ? 'n/d'
                        : `${num(c.anniSopraMercato)} / ${num(anni.length)}`,
                    )}
                    toni={comuni.map(() => 'neutro' as const)}
                    inTesta={inTestaFra(comuni.map((c) => c.anniSopraMercato), {
                      tipo: 'assoluta',
                      valore: 0,
                    })}
                    candidati={candidati}
                  />
                </tbody>
              </table>
            </div>

            <div className="sb-cf-nota" style={{ marginTop: 'var(--sb-s-6)' }}>
              Il <b>drawdown sul periodo comune non c'è</b>, e non è un'omissione: si ricava dalla
              curva del capitale, non dai rendimenti annuali. Il drawdown della tabella è quello di
              tutto il periodo di ciascuno — quindi «a pari rischio» qui non si può ricalcolare
              senza mescolare due finestre diverse.
            </div>
          </>
        )}

        {analisi.scartati.length > 0 && (
          <div className="sb-cf-nota" style={{ marginTop: 'var(--sb-s-6)' }}>
            <b>Anni esclusi dal periodo comune:</b>{' '}
            {analisi.scartati.map((s) => `${s.anno} (${s.motivo})`).join(' · ')}. Abbiamo i
            rendimenti <b>anno per anno</b>, non giorno per giorno: una sovrapposizione parziale
            dentro un anno non si può ricavare senza inventare la parte che manca.
          </div>
        )}
      </div>
    </div>
  );
}

function RigaComune({
  nome,
  valori,
  toni,
  inTesta,
  candidati,
}: {
  nome: string;
  valori: string[];
  toni: ReturnType<typeof tonoRendimento>[];
  /** Indice del candidato in testa, `null` a pari o dove non c'è un merito. */
  inTesta: number | null;
  candidati: Candidato[];
}) {
  return (
    <tr>
      <td>
        <span className="sb-cf-param-chiave">{nome}</span>
      </td>
      {candidati.map((c, i) => (
        <td
          key={c.id}
          className={`sb-tono-${toni[i]}${inTesta === i ? ' sb-cf-migliore' : ''}`}
          style={{ fontWeight: 'var(--sb-peso-forte)' }}
        >
          {valori[i]}
          {inTesta === i && <span className="sb-cf-intesta">in testa</span>}
        </td>
      ))}
    </tr>
  );
}

/* ==========================================================================
 * LA TABELLA DELLE METRICHE, NELLE DUE FORME
 * ======================================================================== */

interface RigaMetrica {
  m: Metrica;
  valori: string[];
  toni: string[];
  /** Il confronto su questa riga è lecito: o è normalizzata, o i periodi coincidono. */
  lecito: boolean;
  migliore: number | null;
  scarto: string | null;
}

const GRUPPI: GruppoMetrica[] = ['rendimento', 'rischio', 'robustezza', 'campione'];

function TabellaMetriche({
  candidati,
  periodiUguali,
}: {
  candidati: Candidato[];
  periodiUguali: boolean;
}) {
  const righe: RigaMetrica[] = useMemo(
    () =>
      METRICHE.map((m) => {
        const esito = esitoRiga(m, candidati);
        const lecito = confrontoLecito(m, periodiUguali);
        return {
          m,
          valori: candidati.map((c) => m.testo(m.valore(c.dato))),
          toni: candidati.map((c) => {
            if (m.tono === 'segno') return `sb-tono-${tonoRendimento(m.valore(c.dato))}`;
            return `sb-tono-${m.tono}`;
          }),
          lecito,
          /* Il migliore si evidenzia SOLO dove il confronto è lecito: su una
             riga cumulativa con periodi diversi, «il migliore» sarebbe chi ha
             misurato più a lungo. */
          migliore: lecito && esito.inTesta.length === 1 ? esito.inTesta[0] : null,
          scarto: lecito ? esito.scarto : null,
        };
      }),
    [candidati, periodiUguali],
  );

  return (
    <>
      {/* --- telefono: una scheda per metrica --- */}
      <div className="sb-lista">
        {GRUPPI.map((g) => (
          <div key={g}>
            <div className="sb-cf-gruppo-titolo">{NOMI_GRUPPO[g]}</div>
            {righe
              .filter((r) => r.m.gruppo === g)
              .map((r) => (
                <div
                  key={r.m.chiave}
                  className="sb-cf-scheda-metrica"
                  style={{ marginTop: 'var(--sb-s-4)' }}
                >
                  <div className="sb-cf-scheda-metrica__nome">{r.m.nome}</div>
                  <div className="sb-cf-tabella__spiega">{r.m.spiega}</div>
                  {!r.lecito && (
                    <span className="sb-cf-avvertenza-riga">
                      Periodi diversi: questo numero è cumulativo, quindi cresce col tempo misurato.
                      Nessun migliore evidenziato — sarebbe il premio alla finestra più lunga.
                    </span>
                  )}
                  <div className="sb-cf-scheda-metrica__righe">
                    {candidati.map((c, i) => (
                      <div
                        key={c.id}
                        className={`sb-cf-riga-valore${r.migliore === c.id ? ' sb-cf-migliore' : ''}`}
                      >
                        <span className="sb-cf-lettera sb-cf-lettera--piccola">{c.lettera}</span>
                        <span className={`sb-cf-tabella__valore ${r.toni[i]}`}>{r.valori[i]}</span>
                        <span className="sb-cf-riga-valore__spazio" />
                        {r.migliore === c.id && <span className="sb-cf-intesta">in testa</span>}
                      </div>
                    ))}
                  </div>
                  {r.scarto && (
                    <div className="sb-cf-scarto" style={{ marginTop: 'var(--sb-s-3)' }}>
                      {r.scarto}
                    </div>
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>

      {/* --- tablet e desktop: colonne affiancate --- */}
      <div className="sb-tabella-guscio">
        <div className="sb-scorri-x">
          <table className="sb-cf-tabella" style={{ minWidth: 380 + candidati.length * 150 }}>
            <thead>
              <tr>
                <th style={{ minWidth: 240 }}>Metrica</th>
                {candidati.map((c) => (
                  <th key={c.id}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span className="sb-cf-lettera sb-cf-lettera--piccola">{c.lettera}</span>#
                      {c.id}
                    </span>
                  </th>
                ))}
                <th>Differenza</th>
              </tr>
            </thead>
            <tbody>
              {GRUPPI.map((g) => (
                <Gruppo
                  key={g}
                  gruppo={g}
                  righe={righe.filter((r) => r.m.gruppo === g)}
                  candidati={candidati}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Gruppo({
  gruppo,
  righe,
  candidati,
}: {
  gruppo: GruppoMetrica;
  righe: RigaMetrica[];
  candidati: Candidato[];
}) {
  return (
    <>
      <tr className="sb-cf-gruppo">
        <td colSpan={candidati.length + 2}>{NOMI_GRUPPO[gruppo]}</td>
      </tr>
      {righe.map((r) => (
        <tr key={r.m.chiave} className={r.lecito ? undefined : 'sb-cf-riga--incomparabile'}>
          <td>
            <div className="sb-cf-tabella__metrica">{r.m.nome}</div>
            <div className="sb-cf-tabella__spiega">{r.m.spiega}</div>
            {!r.lecito && (
              <span className="sb-cf-avvertenza-riga">
                Numero cumulativo su periodi diversi: cresce col tempo misurato, non solo col merito
                della strategia. Nessun migliore evidenziato.
              </span>
            )}
          </td>
          {candidati.map((c, i) => (
            <td key={c.id} className={r.migliore === c.id ? 'sb-cf-migliore' : undefined}>
              <span className={`sb-cf-tabella__valore ${r.toni[i]}`}>{r.valori[i]}</span>
              {r.migliore === c.id && <span className="sb-cf-intesta">in testa</span>}
            </td>
          ))}
          <td>
            <span className="sb-cf-scarto">{r.scarto ?? '—'}</span>
          </td>
        </tr>
      ))}
    </>
  );
}
